import { memo, useCallback, useEffect, useRef, useState } from 'react';

import { AnnouncementBody, type AnnouncementBodyProps } from './AnnouncementBody';
import { AnnouncementGlyph } from './AnnouncementGlyph';
import { type AnnouncementSize, announcementSize } from './announcementSize';
import { announcementTone } from './announcementTone';

export type AnnouncementPillProps = Omit<AnnouncementBodyProps, 'collapseButtonRef' | 'onCollapse'> & {
  shortTitle?: string;
  size?: AnnouncementSize;
};

export const AnnouncementPill = memo(
  ({ className, shortTitle, size = 'small', title, ...rest }: AnnouncementPillProps) => {
    const [expanded, setExpanded] = useState(false);
    const collapsedButtonRef = useRef<HTMLButtonElement>(null);
    const collapseButtonRef = useRef<HTMLButtonElement>(null);
    const hasRenderedRef = useRef<boolean>();
    const collapsedLabel = shortTitle ? shortTitle : title;
    const handleExpand = useCallback(() => {
      setExpanded(true);
    }, []);
    const handleCollapse = useCallback(() => {
      setExpanded(false);
    }, []);

    // Expanding/collapsing swaps the root element, which unmounts the focused button and drops
    // keyboard/AT focus to the document body; move it onto the control that replaced it instead.
    useEffect(() => {
      if (!hasRenderedRef.current) {
        hasRenderedRef.current = true;
        return;
      }

      if (expanded) {
        collapseButtonRef.current?.focus();
      } else {
        collapsedButtonRef.current?.focus();
      }
    }, [expanded]);

    return (
      <div className={announcementSize({ className, size })} data-testid="ANNOUNCEMENT_PILL">
        {expanded ? (
          <AnnouncementBody
            {...rest}
            className="flex w-full items-start gap-2 rounded-12 p-3"
            collapseButtonRef={collapseButtonRef}
            onCollapse={handleCollapse}
            title={title}
          />
        ) : (
          <button
            aria-expanded={false}
            className={announcementTone({
              className: 'inline-flex items-center gap-2 rounded-full px-3 py-1 text-14',
              tone: rest.tone,
            })}
            data-testid="ANNOUNCEMENT_PILL_TRIGGER"
            onClick={handleExpand}
            ref={collapsedButtonRef}
            type="button"
          >
            <AnnouncementGlyph icon={rest.icon} />
            <span>{collapsedLabel}</span>
          </button>
        )}
      </div>
    );
  },
);

AnnouncementPill.displayName = 'AnnouncementPill';
